import React, { forwardRef, useState, useRef, useEffect } from 'react'

const SandwichField = ({
  className='form-control', style,
  forwardType='text', type=forwardType, // So it can be nested in InputField
  prefix, suffix, ...props
}, ref) => {
  const [ padding, setPadding ] = useState({
    paddingInlineStart: '0.75rem',
    paddingInlineEnd: '0.75rem',
    paddingBlockStart: '0.375rem',
    paddingBlockEnd: '0.375rem',
  })

  const mirror = useRef()
  const prefixRef = useRef()
  const suffixRef = useRef()

  const inputProps = {
    ...props, ref, className,
    style: { ...style, ...padding, outline: 0 }
  }

  const inputMarkup = [ 'function', 'object' ].includes(typeof type)
  ? (Input => <Input {...inputProps} />)(type)
  : <input {...inputProps} type={type} />

  const dir = type.dir
  || ([ 'email', 'number', 'tel', 'url' ].includes(type) ? 'ltr' : null)

  const isInvalid = className?.split(' ').includes('is-invalid') ? 'is-invalid' : ''

  const calcPadding = () => {
    // The IntersectionObserver below delivers callbacks asynchronously, so
    // one can fire after the field has unmounted (React has nulled the ref
    // and disconnect() doesn't cancel an already-queued notification) —
    // guard against getComputedStyle(null). A live mirror implies its
    // prefix/suffix children exist too, so the offsetWidth reads are safe.
    if (!mirror.current) return
    const {
      paddingInlineStart,
      paddingInlineEnd,
      paddingBlockStart,
      paddingBlockEnd,
    } = getComputedStyle(mirror.current)
    setPadding({
      paddingInlineStart: `calc(${paddingInlineStart} + ${prefixRef.current.offsetWidth}px)`,
      paddingInlineEnd: `calc(${paddingInlineEnd} + ${suffixRef.current.offsetWidth}px)`,
      paddingBlockStart,
      paddingBlockEnd,
    })
  }

  useEffect(() => {
    calcPadding()
    // Required for Chrome when widget is initially rendered offscreen
    const observer = new IntersectionObserver(entries =>
      entries.forEach(entry => {
        if (entry.isIntersecting)
          calcPadding()
      })
    )
    observer.observe(mirror.current)
    return () => observer.disconnect()
  }, [ className, JSON.stringify(style), prefix, suffix ])

  return (
    <div className={`sandwich-field position-relative ${isInvalid}`}>
      <div
        ref={mirror}
        dir={dir}
        className={`position-absolute w-100 pe-none ${className} bg-transparent`}
        style={{ ...style, borderColor: 'transparent', backgroundImage: 'none' }}
        aria-hidden="true"
      >
        <span ref={prefixRef} className="sandwich-field-prefix">{prefix}</span>
        <span className="sandwich-field-value opacity-0">
          {props.value || props.placeholder}
        </span>
        <span ref={suffixRef} className="sandwich-field-suffix">{suffix}</span>
      </div>
      {inputMarkup}
    </div>
  )
}

export default forwardRef(SandwichField)
